import { create } from 'zustand';
import { clamp } from '@/core/math/grid';
import type { DeviceType } from '@/devices/types';

export const WORKSPACE_MODES = ['design', 'program', 'workshop', 'hmi', 'simulation', 'monitor', 'debug'] as const;
export type WorkspaceMode = (typeof WORKSPACE_MODES)[number];
export const DEFAULT_MODE: WorkspaceMode = 'design';

export const BOTTOM_TABS = [
  'ladder',
  'variables',
  'plcMonitor',
  'alarms',
  'events',
  'diagnostics',
  'modbus',
  'console',
] as const;
export type BottomTab = (typeof BOTTOM_TABS)[number];

export type PanelId = 'left' | 'right' | 'bottom';

/** Kinds of objects that can be selected in the workspace (devices now, ladder/wires later). */
export type SelectableObjectType = DeviceType | 'network' | 'wire' | 'ladderElement' | 'hmiComponent' | 'pipe';

/** Panel size limits in CSS pixels. Reserved for the resize handles added in a later phase. */
export const PANEL_SIZE_LIMITS: Readonly<
  Record<PanelId, { readonly min: number; readonly max: number; readonly initial: number }>
> = {
  left: { min: 200, max: 480, initial: 248 },
  right: { min: 240, max: 520, initial: 288 },
  bottom: { min: 120, max: 480, initial: 190 },
};

/** UI-only state: navigation, layout, selection. Never simulation or PLC state (TD-003). */
export interface UiState {
  readonly activeMode: WorkspaceMode;
  readonly activeBottomTab: BottomTab;

  readonly leftPanelVisible: boolean;
  readonly rightPanelVisible: boolean;
  readonly bottomPanelVisible: boolean;
  readonly panelSizes: Readonly<Record<PanelId, number>>;

  readonly selectedObjectId: string | null;
  readonly selectedObjectType: SelectableObjectType | null;

  readonly settingsOpen: boolean;
  readonly theme: 'light' | 'dark';
  readonly hmiOverlayOpen: boolean;
  readonly hmiOverlayMinimized: boolean;

  setMode: (mode: WorkspaceMode) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setBottomTab: (tab: BottomTab) => void;
  togglePanel: (panel: PanelId) => void;
  setPanelVisible: (panel: PanelId, visible: boolean) => void;
  setPanelSize: (panel: PanelId, size: number) => void;
  select: (id: string, type: SelectableObjectType) => void;
  clearSelection: () => void;
  setSettingsOpen: (open: boolean) => void;
  setHmiOverlayOpen: (open: boolean) => void;
  setHmiOverlayMinimized: (minimized: boolean) => void;
  toggleHmiOverlay: () => void;
}

const visibleKey = {
  left: 'leftPanelVisible',
  right: 'rightPanelVisible',
  bottom: 'bottomPanelVisible',
} as const satisfies Record<PanelId, keyof UiState>;

export const useUiStore = create<UiState>()((set) => ({
  activeMode: DEFAULT_MODE,
  activeBottomTab: 'ladder',

  leftPanelVisible: true,
  rightPanelVisible: true,
  bottomPanelVisible: true,
  panelSizes: {
    left: PANEL_SIZE_LIMITS.left.initial,
    right: PANEL_SIZE_LIMITS.right.initial,
    bottom: PANEL_SIZE_LIMITS.bottom.initial,
  },

  selectedObjectId: null,
  selectedObjectType: null,

  settingsOpen: false,
  theme: 'dark',
  hmiOverlayOpen: false,
  hmiOverlayMinimized: false,

  setMode: (activeMode) => set({ activeMode }),
  setTheme: (theme) => set({ theme }),
  setBottomTab: (activeBottomTab) => set({ activeBottomTab }),
  togglePanel: (panel) => set((state) => ({ [visibleKey[panel]]: !state[visibleKey[panel]] })),
  setPanelVisible: (panel, visible) => set({ [visibleKey[panel]]: visible }),
  setPanelSize: (panel, size) =>
    set((state) => {
      const { min, max } = PANEL_SIZE_LIMITS[panel];
      return { panelSizes: { ...state.panelSizes, [panel]: clamp(size, min, max) } };
    }),
  select: (id, type) => set({ selectedObjectId: id, selectedObjectType: type }),
  clearSelection: () => set({ selectedObjectId: null, selectedObjectType: null }),
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),
  setHmiOverlayOpen: (hmiOverlayOpen) => set({ hmiOverlayOpen }),
  setHmiOverlayMinimized: (hmiOverlayMinimized) => set({ hmiOverlayMinimized }),
  toggleHmiOverlay: () => set((state) => ({ hmiOverlayOpen: !state.hmiOverlayOpen })),
}));
